/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */

import ai.DetailRow
import ai.MathLine
import ai.RealizeAction
import ai.RealizeContent
import ai.ReasonContent
import ai.RevealComparison
import ai.RevealContent
import ai.Wiz3RDetailContent
import ai.Wiz3RDetailContentBuilder
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLElement
import org.w3c.dom.get
import org.w3c.fetch.RequestInit
import kotlin.js.json as jsJson

/**
 * Wiz 3R card detail page — Phase 2b.
 *
 * Builds a [Wiz3RDetailContent] from the card + the user's fresh
 * transactions/goals and renders REVEAL / REASON / REALIZE into the page.
 * Until the fetch returns, the sections show a "loading…" placeholder.
 *
 * Per the portability plan: all filtering/composition logic lives in
 * shared/commonMain (Wiz3RDetailContentBuilder). This file only translates
 * the rendered data classes into HTML; the iOS/Android ports will share
 * the same data side.
 */
class WebWiz3RCardDetailComponent(
    private val navigationManager: WebNavigationManager,
    private val card: PersonalizedCardData?,
    private val anchor: String?,
    private val openChat: Boolean = false,
    private val onBack: () -> Unit,
) {
    private val json = Json { ignoreUnknownKeys = true }
    private var container: HTMLElement? = null
    private var content: Wiz3RDetailContent? = null
    private var loadingError: String? = null
    private var chatOpen: Boolean = openChat
    private var chatPanel: WebWiz3RChatPanel? = null

    fun render(): HTMLElement {
        val el = document.createElement("div") as HTMLElement
        el.style.cssText = baseContainerStyle()
        container = el

        if (card == null) {
            el.innerHTML = renderMissingCard()
            attachBackHandler(el)
            return el
        }

        renderInto(el)

        // Kick off fetch — on completion, re-render in place.
        loadDetail()

        return el
    }

    private fun renderInto(el: HTMLElement) {
        val c = card ?: return
        val t = WebTheme.current

        // Two-column grid on wide viewports when chat is open. The left column
        // hosts the 3R sections; the right column is the chat panel rail
        // (mounted separately after innerHTML).
        val gridStyle = if (chatOpen) {
            "display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: 16px; align-items: start; max-width: 1200px; margin: 0 auto;"
        } else {
            "max-width: 760px; margin: 0 auto;"
        }

        val leftColumn = renderRevealSection(t, c, content?.reveal) +
            renderReasonSection(t, c, content?.reason, c.chart != null) +
            renderRealizeSection(t, c, content?.realize) +
            (if (!chatOpen) renderAskWiz3RButton(t, c) else "") +
            (loadingError?.let { renderErrorBanner(t, it) } ?: "")

        val rightColumn = if (chatOpen) {
            """<div id="wiz3r-chat-mount" style="position: sticky; top: 16px;"></div>"""
        } else ""

        el.innerHTML = renderHeader(t, c) +
            """<div style="$gridStyle">
                 <div>$leftColumn</div>
                 $rightColumn
               </div>"""

        // Inline the existing chart spec into the Reason section, if any.
        c.chart?.let { chart ->
            val mount = el.querySelector("#wiz3r-detail-chart") as? HTMLElement
            mount?.innerHTML = Wiz3RChart.renderToSvg(chart)
        }

        attachBackHandler(el)
        attachActionHandlers(el)
        attachAskWiz3RHandler(el)
        mountChatPanelIfOpen(el)

        anchor?.let { a ->
            window.setTimeout({
                val target = el.querySelector("#wiz3r-section-$a") as? HTMLElement
                target?.scrollIntoView(jsJson("behavior" to "smooth", "block" to "start"))
            }, 50)
        }
    }

    private fun attachAskWiz3RHandler(el: HTMLElement) {
        val btn = el.querySelector("#wiz3r-detail-ask") as? HTMLElement ?: return
        btn.onclick = { _ ->
            chatOpen = true
            container?.let { renderInto(it) }
            Unit
        }
    }

    private fun mountChatPanelIfOpen(el: HTMLElement) {
        if (!chatOpen) return
        val c = card ?: return
        val mount = el.querySelector("#wiz3r-chat-mount") as? HTMLElement ?: return
        val panel = WebWiz3RChatPanel(
            card = c,
            onClose = {
                chatOpen = false
                container?.let { renderInto(it) }
            },
            onSignUp = { navigationManager.navigateTo(Screen.Register) },
        )
        chatPanel = panel
        mount.appendChild(panel.render())
    }

    private fun loadDetail() {
        val c = card ?: return
        GlobalScope.launch {
            try {
                val token = ApiService.getSharedAuthToken() ?: run {
                    loadingError = "Not authenticated."
                    container?.let { renderInto(it) }
                    return@launch
                }
                val userId = ApiService.getSharedUserId() ?: run {
                    loadingError = "User context unavailable."
                    container?.let { renderInto(it) }
                    return@launch
                }
                val txns = fetchTransactions(token)
                val goals = fetchGoals(token, userId)

                // CRITICAL — insight cards must NEVER compare an
                // incomplete (current) month against a complete prior
                // month. Lag by one: "current" = last full month, "previous"
                // = the month before that. Same convention as the server
                // (SpendingAnalyzer.cmpCurrent / cmpPrevious) — see
                // SpendingAnalyzer.kt:296. Previously this code used
                // Date()/now directly which leaked partial-June data into
                // the detail view's "This month" totals even though the
                // Reveal text correctly compared April vs May.
                val now = kotlin.js.Date()
                val nowYear = now.getFullYear()
                val nowMonth = now.getMonth() + 1
                // Step 1: last full month (= the month BEFORE today's
                // partial month). This is what we present as "this".
                val (curYear, curMonth) = if (nowMonth == 1) nowYear - 1 to 12 else nowYear to nowMonth - 1
                // Step 2: month before that.
                val (prevYear, prevMonth) = if (curMonth == 1) curYear - 1 to 12 else curYear to curMonth - 1
                val curPrefix = "${curYear.toString().padStart(4, '0')}-${curMonth.toString().padStart(2, '0')}"
                val prevPrefix = "${prevYear.toString().padStart(4, '0')}-${prevMonth.toString().padStart(2, '0')}"

                val currentMonthTxns = txns.filter { it.date.startsWith(curPrefix) }.map { PlaidTxnAdapter(it) }
                val previousMonthTxns = txns.filter { it.date.startsWith(prevPrefix) }.map { PlaidTxnAdapter(it) }

                content = Wiz3RDetailContentBuilder.build(
                    cardType = c.type,
                    cardActionType = c.actionType,
                    cardActionData = c.actionData,
                    currentMonthTxns = currentMonthTxns,
                    previousMonthTxns = previousMonthTxns,
                    goals = goals.map { GoalAdapter(it) },
                    metrics = c.metrics?.let { MetricsAdapter(it) },
                )
                container?.let { renderInto(it) }
            } catch (e: Throwable) {
                loadingError = e.message ?: "Failed to load detail."
                container?.let { renderInto(it) }
            }
        }
    }

    // --- Network ----------------------------------------------------------

    private suspend fun fetchTransactions(token: String): List<PlaidTransaction> {
        val baseUrl = getBaseUrl()
        val resp = window.fetch(
            "$baseUrl/api/plaid/transactions",
            RequestInit(
                method = "GET",
                headers = jsJson("Authorization" to "Bearer $token", "Content-Type" to "application/json"),
            ),
        ).await()
        if (!resp.ok) error("Transactions fetch failed: ${resp.status}")
        val body = resp.text().await()
        return json.decodeFromString<TransactionsResponse>(body).transactions
    }

    private suspend fun fetchGoals(token: String, userId: String): List<GoalApiPayload> {
        val baseUrl = getBaseUrl()
        val resp = window.fetch(
            "$baseUrl/api/goals/all/$userId",
            RequestInit(
                method = "GET",
                headers = jsJson("Authorization" to "Bearer $token", "Content-Type" to "application/json"),
            ),
        ).await()
        if (!resp.ok) return emptyList()
        val body = resp.text().await()
        return json.decodeFromString<GoalsListPayload>(body).goals
    }

    private fun getBaseUrl(): String {
        val hostname = window.location.hostname
        return if (hostname == "localhost" || hostname == "127.0.0.1") "http://localhost:3000" else ""
    }

    // --- Rendering --------------------------------------------------------

    private fun baseContainerStyle(): String {
        val t = WebTheme.current
        return """
            min-height: 100vh;
            background: ${t.background};
            color: ${t.textPrimary};
            padding: 24px 16px 80px;
            box-sizing: border-box;
        """.trimIndent()
    }

    private fun renderHeader(t: theme.ThemeColors, card: PersonalizedCardData): String {
        val icon = iconEmojiFor(card.icon)
        return """
            <div style="
                max-width: 760px;
                margin: 0 auto 16px;
                display: flex;
                align-items: center;
                gap: 12px;
            ">
                <button
                    id="wiz3r-detail-back"
                    style="
                        background: transparent;
                        border: 1px solid ${t.border};
                        color: ${t.textPrimary};
                        cursor: pointer;
                        font-size: 14px;
                        padding: 6px 10px;
                        border-radius: 6px;
                        line-height: 1;
                    "
                    title="${escape(StringKey.WIZ3R_DETAIL_BACK.tr())}"
                >${escape(StringKey.WIZ3R_DETAIL_BACK.tr())}</button>
                <div style="
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    background: ${card.iconColor}15;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                ">$icon</div>
                <h2 style="
                    color: ${t.textPrimary};
                    margin: 0;
                    font-size: 20px;
                    font-weight: 600;
                ">${escape(card.title)}</h2>
            </div>
        """.trimIndent()
    }

    private fun renderRevealSection(t: theme.ThemeColors, card: PersonalizedCardData, r: RevealContent?): String {
        val text = card.narrative?.reveal ?: ""
        val sourceBlock = if (r == null) {
            placeholderLoadingBlock(t)
        } else {
            renderRevealRows(t, r)
        }
        return sectionShell(t, card, "reveal", StringKey.WIZ3R_SECTION_REVEAL.tr(), text, sourceBlock)
    }

    private fun renderRevealRows(t: theme.ThemeColors, r: RevealContent): String {
        val rowsHtml = if (r.rows.isEmpty()) {
            """<div style="padding: 12px 4px; color: ${t.textSecondary}; font-style: italic;">
                ${escape(r.emptyMessage ?: "No source rows.")}
              </div>"""
        } else {
            r.rows.joinToString("\n") { renderRow(t, it) }
        }
        val comparisonHtml = r.comparison?.let { renderComparison(t, it) } ?: ""
        val subHtml = r.subheading?.let { """<div style="font-size: 12px; color: ${t.textSecondary}; margin-bottom: 8px;">${escape(it)}</div>""" } ?: ""
        return """
            <div style="margin-top: 16px;">
                <div style="
                    font-size: 11px;
                    font-weight: 700;
                    color: ${t.textSecondary};
                    letter-spacing: 0.08em;
                    margin-bottom: 6px;
                ">${escape(r.heading)}</div>
                $subHtml
                $comparisonHtml
                <div style="
                    background: rgba(0,0,0,0.10);
                    border-radius: 8px;
                    padding: 4px 0;
                ">
                    $rowsHtml
                </div>
            </div>
        """.trimIndent()
    }

    private fun renderRow(t: theme.ThemeColors, row: DetailRow): String {
        val amountColor = row.amountColorHint ?: t.textPrimary
        val subHtml = row.subtitle?.let { """<div style="font-size: 11px; color: ${t.textSecondary};">${escape(it)}</div>""" } ?: ""
        return """
            <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                padding: 8px 12px;
                border-bottom: 1px solid ${t.border}33;
            ">
                <div style="min-width: 0; flex: 1;">
                    <div style="font-size: 13px; color: ${t.textPrimary};">${escape(row.title)}</div>
                    $subHtml
                </div>
                <div style="font-size: 13px; font-weight: 600; color: $amountColor; white-space: nowrap;">${escape(row.amountText)}</div>
            </div>
        """.trimIndent()
    }

    private fun renderComparison(t: theme.ThemeColors, c: RevealComparison): String {
        val deltaHtml = c.deltaText?.let { """<div style="font-size: 11px; color: ${t.textSecondary}; margin-top: 4px;">${escape(it)}</div>""" } ?: ""
        return """
            <div style="
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
                padding: 10px 12px;
                margin-bottom: 10px;
                background: rgba(0,0,0,0.10);
                border-radius: 8px;
            ">
                <div>
                    <div style="font-size: 10px; color: ${t.textSecondary}; text-transform: uppercase; letter-spacing: 0.08em;">${escape(c.currentLabel)}</div>
                    <div style="font-size: 16px; font-weight: 600; color: ${t.textPrimary}; margin-top: 2px;">${escape(c.currentValue)}</div>
                </div>
                <div>
                    <div style="font-size: 10px; color: ${t.textSecondary}; text-transform: uppercase; letter-spacing: 0.08em;">${escape(c.previousLabel)}</div>
                    <div style="font-size: 16px; font-weight: 600; color: ${t.textPrimary}; margin-top: 2px;">${escape(c.previousValue)}</div>
                </div>
                <div style="grid-column: 1 / -1;">$deltaHtml</div>
            </div>
        """.trimIndent()
    }

    private fun renderReasonSection(t: theme.ThemeColors, card: PersonalizedCardData, r: ReasonContent?, hasChart: Boolean): String {
        val text = card.narrative?.reason ?: ""
        val chartHtml = if (hasChart) {
            """
                <div id="wiz3r-detail-chart" style="
                    margin-top: 14px;
                    padding: 8px;
                    background: rgba(0,0,0,0.18);
                    border-radius: 8px;
                "></div>
            """.trimIndent()
        } else ""
        val mathHtml = if (r == null) placeholderLoadingBlock(t) else renderMathLines(t, r)
        return sectionShell(t, card, "reason", StringKey.WIZ3R_SECTION_REASON.tr(), text, chartHtml + mathHtml)
    }

    private fun renderMathLines(t: theme.ThemeColors, r: ReasonContent): String {
        if (r.mathLines.isEmpty() && r.explainer == null) {
            return """<div style="margin-top: 12px; padding: 12px 4px; color: ${t.textSecondary}; font-style: italic;">No additional math available for this card.</div>"""
        }
        val linesHtml = r.mathLines.joinToString("\n") { renderMathLine(t, it) }
        val explainerHtml = r.explainer?.let {
            """<div style="
                margin-top: 12px;
                padding: 10px 12px;
                font-size: 12px;
                color: ${t.textSecondary};
                background: rgba(0,0,0,0.08);
                border-radius: 8px;
                line-height: 1.5;
            ">${escape(it)}</div>"""
        } ?: ""
        return """
            <div style="margin-top: 14px;">
                <div style="
                    font-size: 11px;
                    font-weight: 700;
                    color: ${t.textSecondary};
                    letter-spacing: 0.08em;
                    margin-bottom: 6px;
                ">${escape(StringKey.WIZ3R_KEY_INPUTS.tr())}</div>
                <div style="background: rgba(0,0,0,0.10); border-radius: 8px; padding: 4px 0;">
                    $linesHtml
                </div>
                $explainerHtml
            </div>
        """.trimIndent()
    }

    private fun renderMathLine(t: theme.ThemeColors, m: MathLine): String {
        val exprHtml = m.expression?.let {
            """<div style="font-size: 11px; color: ${t.textSecondary}; font-family: monospace;">${escape(it)}</div>"""
        } ?: ""
        return """
            <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                padding: 8px 12px;
                border-bottom: 1px solid ${t.border}33;
            ">
                <div style="min-width: 0;">
                    <div style="font-size: 13px; color: ${t.textPrimary};">${escape(m.label)}</div>
                    $exprHtml
                </div>
                <div style="font-size: 13px; font-weight: 600; color: ${t.textPrimary};">${escape(m.result)}</div>
            </div>
        """.trimIndent()
    }

    private fun renderRealizeSection(t: theme.ThemeColors, card: PersonalizedCardData, r: RealizeContent?): String {
        val text = card.narrative?.realize ?: ""
        val actionsHtml = if (r == null) {
            placeholderLoadingBlock(t)
        } else {
            renderRealizeActions(t, card, r)
        }
        return sectionShell(t, card, "realize", StringKey.WIZ3R_SECTION_REALIZE.tr(), text, actionsHtml)
    }

    private fun renderRealizeActions(t: theme.ThemeColors, card: PersonalizedCardData, r: RealizeContent): String {
        if (r.actions.isEmpty() && r.intro == null) {
            return """<div style="margin-top: 12px; padding: 12px 4px; color: ${t.textSecondary}; font-style: italic;">No specific actions surfaced for this card yet.</div>"""
        }
        val introHtml = r.intro?.let { """<div style="margin-top: 12px; margin-bottom: 8px; font-size: 13px; color: ${t.textSecondary};">${escape(it)}</div>""" } ?: ""
        val buttonsHtml = r.actions.joinToString("\n") { renderRealizeButton(card, it) }
        return """
            <div style="margin-top: 4px;">
                $introHtml
                <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px;">
                    $buttonsHtml
                </div>
            </div>
        """.trimIndent()
    }

    private fun renderRealizeButton(card: PersonalizedCardData, a: RealizeAction): String {
        val border = if (a.primary) card.iconColor else "${card.iconColor}66"
        val bg = if (a.primary) "${card.iconColor}18" else "transparent"
        val data = a.actionData.entries.joinToString(" ") { (k, v) ->
            """data-action-$k="${escape(v)}""""
        }
        return """
            <button
                class="wiz3r-realize-action"
                data-action-type="${escape(a.actionType)}"
                $data
                style="
                    padding: 10px 14px;
                    background: $bg;
                    border: 1px solid $border;
                    color: ${card.iconColor};
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background 0.15s;
                "
                onmouseover="this.style.background='${card.iconColor}28'"
                onmouseout="this.style.background='$bg'"
            >${escape(a.label)} →</button>
        """.trimIndent()
    }

    private fun sectionShell(
        t: theme.ThemeColors,
        card: PersonalizedCardData,
        anchorId: String,
        label: String,
        narrativeText: String,
        innerHtml: String,
    ): String {
        return """
            <section
                id="wiz3r-section-$anchorId"
                style="
                    max-width: 760px;
                    margin: 0 auto 16px;
                    padding: 20px 22px;
                    background: ${t.card};
                    border: 1px solid ${t.border};
                    border-left: 4px solid ${card.iconColor};
                    border-radius: 10px;
                    box-shadow: ${t.cardShadow};
                "
            >
                <div style="
                    font-size: 11px;
                    font-weight: 700;
                    color: ${card.iconColor};
                    letter-spacing: 0.12em;
                    margin-bottom: 8px;
                ">$label</div>
                <div style="
                    font-size: 15px;
                    line-height: 1.55;
                    color: ${t.textPrimary};
                ">${escape(narrativeText)}</div>
                $innerHtml
            </section>
        """.trimIndent()
    }

    private fun renderAskWiz3RButton(t: theme.ThemeColors, card: PersonalizedCardData): String {
        return """
            <div style="
                margin: 24px auto 0;
                display: flex;
                justify-content: center;
            ">
                <button
                    id="wiz3r-detail-ask"
                    style="
                        padding: 12px 22px;
                        background: ${card.iconColor}18;
                        border: 1px solid ${card.iconColor};
                        color: ${card.iconColor};
                        border-radius: 8px;
                        font-size: 14px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: background 0.15s;
                    "
                    onmouseover="this.style.background='${card.iconColor}28'"
                    onmouseout="this.style.background='${card.iconColor}18'"
                >${escape(StringKey.WIZ3R_ASK.tr())}</button>
            </div>
        """.trimIndent()
    }

    private fun placeholderLoadingBlock(t: theme.ThemeColors): String {
        return """
            <div style="
                margin-top: 14px;
                padding: 14px;
                background: rgba(0,0,0,0.08);
                border-radius: 8px;
                color: ${t.textSecondary};
                font-size: 12px;
                font-style: italic;
            ">${escape(StringKey.WIZ3R_LOADING_SOURCE_DATA.tr())}</div>
        """.trimIndent()
    }

    private fun renderErrorBanner(t: theme.ThemeColors, msg: String): String {
        return """
            <div style="
                max-width: 760px;
                margin: 12px auto 0;
                padding: 10px 14px;
                background: #b3261e15;
                border: 1px solid #b3261e55;
                color: #ff8a80;
                font-size: 12px;
                border-radius: 8px;
            ">${escape(msg)}</div>
        """.trimIndent()
    }

    private fun renderMissingCard(): String {
        val t = WebTheme.current
        return """
            <div style="
                max-width: 520px;
                margin: 80px auto;
                text-align: center;
                color: ${t.textSecondary};
            ">
                <h2 style="color: ${t.textPrimary}; margin-bottom: 12px;">${escape(StringKey.WIZ3R_CARD_NOT_LOADED.tr())}</h2>
                <p style="margin-bottom: 20px;">${escape(StringKey.WIZ3R_CARD_NOT_LOADED_BODY.tr())}</p>
                <button
                    id="wiz3r-detail-back"
                    style="
                        background: transparent;
                        border: 1px solid ${t.border};
                        color: ${t.textPrimary};
                        cursor: pointer;
                        font-size: 14px;
                        padding: 8px 14px;
                        border-radius: 6px;
                    "
                >${escape(StringKey.WIZ3R_BACK_TO_DASHBOARD.tr())}</button>
            </div>
        """.trimIndent()
    }

    // --- Event wiring -----------------------------------------------------

    private fun attachBackHandler(el: HTMLElement) {
        val backBtn = el.querySelector("#wiz3r-detail-back") as? HTMLElement
        backBtn?.onclick = { _ ->
            onBack()
            Unit
        }
    }

    private fun attachActionHandlers(el: HTMLElement) {
        val buttons = el.querySelectorAll(".wiz3r-realize-action")
        for (i in 0 until buttons.length) {
            val btn = buttons[i] as? HTMLElement ?: continue
            btn.onclick = { event ->
                event.preventDefault()
                val actionType = btn.getAttribute("data-action-type") ?: ""
                val category = btn.getAttribute("data-action-category") ?: ""
                val goalId = btn.getAttribute("data-action-goalId") ?: ""
                val merchants = btn.getAttribute("data-action-merchants") ?: ""
                handleRealizeAction(actionType, category, goalId, merchants)
            }
        }
    }

    private fun handleRealizeAction(actionType: String, category: String, goalId: String, merchants: String) {
        when (actionType) {
            "NAVIGATE_TRANSACTIONS" -> navigationManager.navigateTo(Screen.TransactionList())
            "NAVIGATE_CATEGORY" -> navigationManager.navigateTo(Screen.TransactionList(categoryFilter = category.ifEmpty { null }))
            "NAVIGATE_GOAL" -> {
                navigationManager.switchToTab(ui.AppTabType.GOALS)
                navigationManager.navigateTo(Screen.SavingsGoals())
            }
            "CREATE_GOAL" -> navigationManager.navigateTo(Screen.SavingsGoals(openCreateDialog = true))
            "NAVIGATE_GUIDE", "NAVIGATE_GUIDE_GOALS" -> navigationManager.navigateTo(Screen.Guide)
            "NAVIGATE_BUDGET" -> {
                navigationManager.setPendingBudgetFocusCategory(category.ifEmpty { null })
                navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
            }
            "REVIEW_SUBSCRIPTIONS" -> {
                // Land on transactions for now; review modal lives on the dashboard.
                navigationManager.navigateTo(Screen.TransactionList())
            }
            else -> {}
        }
    }

    // --- HTML escaping ----------------------------------------------------

    private fun escape(s: String): String =
        HtmlEscape.escape(s)

    // --- Adapters ---------------------------------------------------------

    private class PlaidTxnAdapter(private val t: PlaidTransaction) : Wiz3RDetailContentBuilder.TxnLike {
        override val merchant: String get() = t.merchant_name ?: t.name
        override val date: String get() = t.date
        override val amount: Double get() = t.amount

        /** Effective category code for grouping/filtering. Mirrors the
         *  server's [SpendingAnalyzer.calculateCategorySpending] logic:
         *  honour the user's category override first (format "Primary/Detailed",
         *  we split on "/"), then fall back to Plaid's raw primary. Without
         *  this, the REVEAL data block (web-side) and the card metrics
         *  (server-side) diverge as soon as the user re-categorizes anything. */
        override val categoryCode: String?
            get() = t.user_category?.split("/")?.firstOrNull()
                ?: t.personal_finance_category?.primary

        /** Localized label for display. Uses CategoryUtils for proper splits
         *  (RENT_AND_UTILITIES → Rent vs Utilities, etc.) when category came
         *  from Plaid. When the user has overridden the category, we surface
         *  the override verbatim — that's what they typed. */
        override val categoryLabel: String?
            get() {
                t.user_category?.split("/")?.firstOrNull()?.let { return it }
                val primary = t.personal_finance_category?.primary ?: return null
                val detailed = t.personal_finance_category?.detailed ?: primary
                return utils.CategoryUtils.getEffectiveDisplayCategory(primary, detailed)
            }

        override val assignedGoalId: String? get() = t.assigned_goal_id
        override val assignedGoalName: String? get() = t.assigned_goal_name
        override val assignedGoalAmount: Double? get() = t.assigned_goal_amount
        override val excluded: Boolean get() = models.TransactionEligibility.excludeFromTotals(
            isSplitParent = t.is_split_parent,
            isDistributedParent = t.is_distributed_parent,
            isExcluded = t.is_excluded,
        )
    }

    private class GoalAdapter(private val g: GoalApiPayload) : Wiz3RDetailContentBuilder.GoalLike {
        override val id: String get() = g.id
        override val name: String get() = g.name
        override val currentAmount: Double get() = g.current_amount
        override val targetAmount: Double get() = g.target_amount
    }

    private class MetricsAdapter(private val m: CardMetricsData) : Wiz3RDetailContentBuilder.CardMetricsLike {
        override val amount: Double? get() = m.amount
        override val percentOfIncome: Double? get() = m.percentOfIncome
        override val percentChange: Double? get() = m.percentChange
        override val savingsRate: Double? get() = m.savingsRate
        override val previousSavingsRate: Double? get() = m.previousSavingsRate
        override val goalProgress: Double? get() = m.goalProgress
        override val category: String? get() = m.category
        override val percentOfBudget: Double? get() = m.percentOfBudget
        override val plannedAmount: Double? get() = m.plannedAmount
    }

    private fun iconEmojiFor(iconName: String): String = when (iconName) {
        "warning" -> "⚠️"
        "trending_up" -> "📈"
        "trending_down" -> "📉"
        "star" -> "⭐"
        "thumb_up" -> "👍"
        "check_circle" -> "✅"
        "schedule" -> "⏰"
        "emoji_events" -> "🏆"
        "celebration" -> "🎉"
        "weekend" -> "🎯"
        "subscriptions" -> "🔄"
        "lightbulb" -> "💡"
        "waving_hand" -> "👋"
        "flag" -> "🚩"
        "diamond" -> "💎"
        else -> "💡"
    }

}

/**
 * Minimal payload for the goals endpoint. Stays in this file so the detail
 * component can decode it without disturbing the SavingsGoals* type tree.
 */
@kotlinx.serialization.Serializable
data class GoalApiPayload(
    val id: String,
    val name: String,
    val target_amount: Double,
    val current_amount: Double,
)

@kotlinx.serialization.Serializable
data class GoalsListPayload(
    val goals: List<GoalApiPayload>,
)
